/**
 * When the queue gets incorporated, this will be available...
 */
import Queue from "@segment/localstorage-retry";
import uuid from "uuid/v4";
import {
  CustomerRequest,
  CustomerResponseObject,
  CustomerResponseError,
  CustomerResponseSuccess
} from "../types";
import debug from "./debug";
import appEvents from "./appEvents";
import api, { TEST_KEYWORDS } from "./api";
import { addNotification } from "./notifications";

export const REQUEST_ID_IS_RETRYING: { [propName: string]: number } = {};

export const MAX_ATTEMPTS = 10;

const QUEUE_OPTIONS = {
  minRetryDelay: 10 * 1000, // min retry delay in ms (used in exp. backoff calcs)
  maxRetryDelay: 30 * 60 * 1000, // max retry delay in ms (used in exp. backoff calcs)
  backoffFactor: 2, // exponential backoff factor (attempts^n)
  backoffJitter: 0, // jitter factor for backoff calcs (0 is usually fine)
  maxItems: 100, // queue high water mark (we suggest 100 as a max)
  maxAttempts: MAX_ATTEMPTS // max retry attempts before discarding
};

const queue = new Queue(
  "retryCommittingCustomerData",
  QUEUE_OPTIONS,
  (
    customerReq: CustomerRequest,
    done: (
      customerResError: CustomerResponseObject | null,
      customerResSuccess: CustomerResponseObject | null,
      customerReq: CustomerRequest
    ) => void
  ) => {
    // For each retried request, generate a new traceToken and increment the attempt
    customerReq.traceToken = uuid();
    customerReq.attempt++;

    // Mark that the request is currently being retried/queued
    REQUEST_ID_IS_RETRYING[customerReq.requestId] = customerReq.attempt;

    debug("[queue] Retry commiting customer data", { customerReq });

    api
      .commitCustomerData(customerReq)
      .then((customerRes: CustomerResponseObject) => {
        debug("[queue] commitCustomerData: res:", customerRes);
        // If an error occurred during retry, send a specific analytics event
        // (only if familyName is not a special test keyword)
        if (
          customerReq.data.familyName &&
          !TEST_KEYWORDS.includes(customerReq.data.familyName) &&
          (customerRes.errorType === "api_error" ||
            customerRes.errorType === "failure")
        ) {
          appEvents.trigger("analytics.error", {
            category: "customer_data",
            action: "retry_commit_customer_data",
            fatal: customerRes.errorType === "failure",
            label: [customerRes.errorCode, customerRes.errorMessage]
              .filter(n => n)
              .join(" - "),
            value: customerReq.attempt,
            dimensions: {
              traceToken: customerRes.traceToken,
              requestId: customerReq.requestId,
              salesPersonId: customerReq.salesPersonId,
              storeId: customerReq.storeId,
              storeCountry: customerReq.storeCountry,
              attempt: customerReq.attempt,
              storeName: customerReq.storeName,
              countryName: customerReq.countryName
            }
          });
        }

        // Error type of api_error should allow the queue to
        // retry by firing the callback with the response in
        // the customerResError param.
        // Once the maximum number of error responses have
        // occurred, then the queue will automatically abort
        // further attempts.
        if (customerRes.errorType === "api_error") {
          return done(customerRes, null, customerReq);
        }

        // Either successful customer response or failure occurred
        // which results in aborting any ability to automatically
        // retry by sending the response as the customerResSuccess
        // param.
        done(null, customerRes, customerReq);
      });
  }
);

// Either retry the request again if it non-fatally errored, or stop
// the retry after success/fatal error.
queue.on(
  "processed",
  (
    customerResError: CustomerResponseError,
    customerResSuccess: CustomerResponseSuccess,
    customerReq: CustomerRequest
  ) => {
    if (customerResError) {
      debug("[queue] processing failed with error", {
        customerReq,
        customerResError
      });

      addNotification(customerReq, customerResError);
    } else {
      debug("[queue] processing completed", {
        customerReq,
        customerResSuccess
      });

      // Remove from the marked retrying cache
      delete REQUEST_ID_IS_RETRYING[customerReq.requestId];

      addNotification(customerReq, customerResSuccess);
    }
  }
);

// If a queued item has exceeded maximum retries, it will be discarded
queue.on("discard", (customerReq: CustomerRequest, attempts: number) => {
  debug("[queue] discarded item", {
    customerReq,
    attempts
  });

  // Remove from the marked retrying cache
  delete REQUEST_ID_IS_RETRYING[customerReq.requestId];
});

// Only run if not in a test environment
if (process.env.NODE_ENV !== "test") {
  queue.start();
}

export default queue;
